/* eslint-disable jsx-a11y/label-has-associated-control */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  GetInfluencerProductRequestResponse,
  IProductRequestSettings,
} from '@Saral-Influencer-Marketing/saral-utils/dist/types/productRequests';
import EnhancedModal from '../../common/EnhancedModal/EnhancedModal';
import styles from './requestProductModal.module.css';
import ProductsPreviewSection from './ProductsPreviewSection/ProductsPreviewSection';
import { createProductRequest, getProductsListInfo } from '../../../services/dashboard.service';
import { getOrderDetails, getProductPrice } from '../../../utils/shipping-utils';
import ModalCommonButtonsContainer from '../../common/ModalComponent/ModalCommonButtonsContainer';
import CartIcon from '../../../assets/icons/CartIcon';
import { REQUEST_PRODUCT_MODAL_TABS, REQUEST_PRODUCT_MODAL_TABS_VALUES } from '../../../utils/constants';
import VaraintSelectionModal from './VariantSelectionModal';
import ConfirmProducts from './ConfirmProducts/ConfirmProducts';
import AddressUpdateModal from '../addressUpdateModal/AddressUpdateModal';
import useIsMobile from '../../../hooks/useIsMobile';
import ButtonComponent from '../../common/button-component/button-component';
import { formatCurrency } from '../../../utils/global';
import ProductSection from './ProductSearch/ProductSection';
import { PreviewProduct } from './ProductSearch/PreviewProduct';
import SearchFTUX from '../../common/SearchFTUX/SearchFTUX';
import AlreadyRequestedProducts from './AlreadyRequestedProducts/AlreadyRequestedProducts';
import ViewProductsModal from './AlreadyRequestedProducts/ViewProductsModal';

interface Props {
  open: boolean;
  onClose: () => void;
  productRequestSettings: IProductRequestSettings;
  defaultCurrency: string;
  address: any;
  profileId: string;
  brandName: string;
}

export default function RequestProductsModal({
  open,
  onClose,
  productRequestSettings,
  defaultCurrency,
  address,
  profileId,
  brandName,
}: Props) {
  const [activeTab, setActiveTab] = useState(REQUEST_PRODUCT_MODAL_TABS_VALUES.AVAILABLE_PRODUCTS);
  const [selectedProductsForOrder, setSelectedProductsForOrder] = useState<any>([]);
  const [userSelectedProducts, setUserSelectedProducts] = useState<any>([]);
  const [products, setProducts] = useState<any>([]);
  const [allOptions, setAllOptions] = useState<any>({});
  const [quantities, setQuantities] = useState<any>({});
  const [maxQuantities, setMaxQuantities] = useState<any>({});
  const [showVariantselectionModal, setShowVariantselectionModal] = useState<boolean>(false);
  const [allOrderProducts, setAllOrderProducts] = useState<any>([]);
  const [isFetchingVariants, setIsFetchingVariants] = useState<boolean>(false);
  const [previewProducts, setPreviewProducts] = useState<any>([]);
  const [searchStates, setSearchStates] = useState<{ isLoading: boolean; searchQuery: string | undefined }>({
    isLoading: false,
    searchQuery: undefined,
  });
  const [isScrolling, setIsScrolling] = useState<boolean>(false);

  const [showConfirmProductsModal, setShowConfirmProductsModal] = useState<boolean>(false);
  const [showConfirmAddressModal, setShowConfirmAddressModal] = useState<boolean>(false);
  const [isSendingProductRequest, setIsSendingProductRequest] = useState<boolean>(false);
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [showViewProductModal, setShowViewProductModal] = useState<GetInfluencerProductRequestResponse | null>(null);
  const isMobile = useIsMobile();

  const isStoreWiseSelection = useMemo(() => {
    return productRequestSettings?.store_level;
  }, [productRequestSettings]);

  const finalTabs = useMemo(() => {
    if (isStoreWiseSelection) {
      return REQUEST_PRODUCT_MODAL_TABS.map((tab) => {
        if (tab.value === REQUEST_PRODUCT_MODAL_TABS_VALUES.AVAILABLE_PRODUCTS) {
          return {
            ...tab,
            label: 'Select Products',
          };
        }
        return tab;
      });
    }
    return REQUEST_PRODUCT_MODAL_TABS;
  }, [isStoreWiseSelection]);

  const handleUpdateSearchStates = <K extends keyof { isLoading: boolean; searchQuery: string | undefined }>(
    key: K,
    value: { isLoading: boolean; searchQuery: string | undefined }[K]
  ) => {
    setSearchStates((prevState) => ({ ...prevState, [key]: value }));
  };

  const fetchSelectedProducts = async (productIds?: number[], isAllOrderProducts = false) => {
    setIsFetchingVariants(true);

    const body = { productIds: productIds || previewProducts.map((product: any) => product.id) };

    const response = await getProductsListInfo(body);
    if (response?.status) {
      setPreviewProducts(response.data);
      setSelectedProductsForOrder(response.data);
      if (isAllOrderProducts) {
        setAllOrderProducts(response.data);
      }
      const quantityObj: Record<string, number> = {};

      response.data.forEach((data: any) => {
        const id = data.variantId || data.productId;
        quantityObj[id] = 0;
      });
      if (!productIds) {
        setQuantities(quantityObj);
        setShowVariantselectionModal(true);
      }
      handleUpdateSearchStates('searchQuery', '');
    } else {
      toast.error('Something went wrong');
    }
    setIsFetchingVariants(false);
  };

  const removeProduct = (productId: string) => {
    setSelectedProductsForOrder(selectedProductsForOrder.filter((product: any) => product.id !== productId));
  };
  // remove selectedProduct
  const removeSelectedProduct = (id: number) => {
    setSelectedProductIds(selectedProductIds.filter((productId: number) => productId !== id));
    setPreviewProducts(previewProducts?.filter((product: any) => product?.id !== id));
  };

  useEffect(() => {
    if (productRequestSettings) {
      const productIds = productRequestSettings?.product_details?.map((order: any) => order.productId);
      setUserSelectedProducts(productRequestSettings.product_details);
      if (productIds?.length > 0) {
        fetchSelectedProducts(productIds, true);
      }

      setAllOptions(productRequestSettings.all_options ? productRequestSettings.all_options : {});

      const quantityObj: any = {};
      productRequestSettings.product_details.forEach((order: any) => {
        const id = order.variantId || order.productId;
        const value = order.quantity;
        quantityObj[id] = value;
      });
      setMaxQuantities(quantityObj);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productRequestSettings]);

  const orderDetails = getOrderDetails(selectedProductsForOrder, allOptions, quantities);

  const getPreviewProductsTotal = (productArray: any[]) => {
    return productArray.reduce((acc: number, product: any) => acc + getProductPrice(product, allOptions), 0);
  };

  const handlePreviewProductsChange = async (productArray: any[]) => {
    const maxOrderValue = productRequestSettings?.store_level_limits?.max_order_value;
    if (maxOrderValue && getPreviewProductsTotal(productArray) > maxOrderValue) {
      toast.error('Maximum order value reached');
      return;
    }

    const productsNeedingDetails = productArray.filter((product) => !product.variants);
    if (productsNeedingDetails.length > 0) {
      setIsFetchingVariants(true);
      const response = await getProductsListInfo({ productIds: productsNeedingDetails.map((product) => product.id) });
      setIsFetchingVariants(false);

      if (response?.status) {
        const fetchedProductsById = Object.fromEntries(response.data.map((product: any) => [product.id, product]));
        const enrichedProducts = productArray.map((product) => fetchedProductsById[product.id] || product);

        if (maxOrderValue && getPreviewProductsTotal(enrichedProducts) > maxOrderValue) {
          toast.error('Maximum order value reached');
          return;
        }

        setPreviewProducts(enrichedProducts);
        setSelectedProductIds(enrichedProducts.map((product: any) => product.id));
        return;
      }
    }

    setPreviewProducts(productArray);
    setSelectedProductIds(productArray.map((product: any) => product.id));
  };

  const { isDisabled } = useMemo(() => {
    if (isStoreWiseSelection) {
      return { isDisabled: previewProducts.length === 0 };
    }

    const quantityValues = Object.values(quantities);
    const isZeroQuantity = quantityValues?.every((quantity: any) => Number(quantity) <= 0);
    return { isDisabled: isZeroQuantity };
  }, [quantities, isStoreWiseSelection, previewProducts]);

  const handleSelectVariant = () => {
    setIsFetchingVariants(true);

    if (isStoreWiseSelection) {
      fetchSelectedProducts();
      return;
    }
    const productsWithMoreQuantity = userSelectedProducts.filter((product: any) => {
      return quantities[product.variantId || product.productId] > 0;
    });

    const filteredProducts = selectedProductsForOrder.filter((product: any) => {
      return productsWithMoreQuantity.some((p: any) => p.productId === product.id);
    });
    setSelectedProductsForOrder(filteredProducts);
    setShowVariantselectionModal(true);
    setIsFetchingVariants(false);
  };

  const createProductRequestForInfluencer = async () => {
    setIsSendingProductRequest(true);
    const updatedOrderDetails = orderDetails.map((order: any) => {
      return {
        ...order,
        quantity: order?.quantity || 1,
      };
    });
    const body = {
      order_details: updatedOrderDetails,
    };

    const response = await createProductRequest(body);
    if (response.status) {
      toast.success('Product Request Sent');
      setShowConfirmProductsModal(false);
      setShowConfirmAddressModal(false);
      setShowVariantselectionModal(false);
      onClose();
    } else {
      toast.error('Something went wrong');
    }
    setIsSendingProductRequest(false);
  };

  const totalSelectedProducts = useMemo(() => {
    if (isStoreWiseSelection) {
      return previewProducts.length;
    }
    const total = Object.values(quantities).reduce((acc: number, product: any) => acc + (product?.quantity || 1), 0);
    return total;
  }, [quantities, isStoreWiseSelection, previewProducts]);

  const { title, description } = useMemo(() => {
    if (isStoreWiseSelection) {
      return {
        title: `Select Products from the ${brandName} Store`,
        description: `You can select up to ${
          productRequestSettings.store_level_limits.max_products_per_request
        } products with a total order value of ${formatCurrency(
          productRequestSettings.store_level_limits.max_order_value || 0,
          defaultCurrency
        )}`,
      };
    }

    return {
      title: 'Request Products',
      description: undefined,
    };
  }, [
    brandName,
    isStoreWiseSelection,
    productRequestSettings.store_level_limits.max_products_per_request,
    productRequestSettings.store_level_limits.max_order_value,
    defaultCurrency,
  ]);

  if (showViewProductModal) {
    return (
      <ViewProductsModal
        open={Boolean(showViewProductModal)}
        onClose={() => setShowViewProductModal(null)}
        defaultCurrency={defaultCurrency}
        products={showViewProductModal?.order_details || []}
      />
    );
  }

  if (showConfirmAddressModal) {
    return (
      <AddressUpdateModal
        address={address}
        setShowUpdateAddressModal={setShowConfirmAddressModal}
        fetchHomePageData={() => {}}
        profileId={profileId}
        modalTitle="Confirm your Shipping Address"
        actionButtonText="Request Products"
        cancelButtonText="Go Back"
        onSaveComplete={() => createProductRequestForInfluencer()}
        showBackButton={!isMobile}
        handleBack={() => {
          setShowConfirmAddressModal(false);
          setShowConfirmProductsModal(true);
        }}
        showLoading={isSendingProductRequest}
        showSuccessMessage={false}
      />
    );
  }

  if (showConfirmProductsModal) {
    return (
      <ConfirmProducts
        open={showConfirmProductsModal}
        onClose={() => {
          setShowConfirmProductsModal(false);
          setShowVariantselectionModal(false);
          onClose();
        }}
        handleBack={() => {
          setShowConfirmProductsModal(false);
          setShowVariantselectionModal(true);
        }}
        orderDetails={orderDetails}
        defaultCurrency={defaultCurrency}
        removeProduct={removeProduct}
        onSave={() => {
          setShowConfirmProductsModal(false);
          setShowConfirmAddressModal(true);
        }}
      />
    );
  }

  if (showVariantselectionModal) {
    return (
      <VaraintSelectionModal
        show={showVariantselectionModal}
        onClose={() => {
          handleUpdateSearchStates('searchQuery', '');
          setProducts([]);
          if (activeTab === REQUEST_PRODUCT_MODAL_TABS_VALUES.REQUESTED_PRODUCTS) {
            setPreviewProducts([]);
          } else {
            setShowVariantselectionModal(false);
          }
          setSelectedProductsForOrder(allOrderProducts);
          onClose();
        }}
        handleBack={() => {
          setShowVariantselectionModal(false);
          setSelectedProductsForOrder(allOrderProducts);
        }}
        onSave={() => {
          setShowConfirmProductsModal(true);
          setShowVariantselectionModal(false);
        }}
        defaultCurrency={defaultCurrency}
        quantities={quantities}
        setQuantities={setQuantities}
        selectedProducts={selectedProductsForOrder}
        setSelectedProducts={setSelectedProductsForOrder}
        allOptions={allOptions}
        setAllOptions={setAllOptions}
        removeProduct={removeProduct}
        disableQuantitySelection
      />
    );
  }

  const renderAlreadyRequestedTab = () => {
    return (
      <AlreadyRequestedProducts defaultCurrency={defaultCurrency} setShowViewProductModal={setShowViewProductModal} />
    );
  };

  const renderSelectionTab = () => {
    return isStoreWiseSelection ? (
      <div className={isMobile ? 'px-4' : 'px-6'}>
        <ProductSection
          products={products}
          setProducts={setProducts}
          previewProducts={previewProducts}
          setPreviewProducts={handlePreviewProductsChange}
          searchStates={{
            isLoading: searchStates.isLoading,
            searchQuery: searchStates.searchQuery || '',
          }}
          handleUpdateSearchStates={(key: any, value: any) => {
            if (key === 'isLoading') {
              handleUpdateSearchStates('isLoading', value);
            } else if (key === 'searchQuery') {
              handleUpdateSearchStates('searchQuery', value);
            }
          }}
          defaultCurrency={defaultCurrency}
          maxLimit={productRequestSettings.store_level_limits.max_products_per_request}
          popOverHeight="236px"
          label="Select Products to request"
        />
        {previewProducts?.length > 0 ? (
          <div className="mt-4">
            <label className="text-subdued mb-0 text-left text-sm w-full mt-2">Selected Products</label>
            <div className={`${styles.shipFreeProductList} flex-1`}>
              <div className={`${styles.shipFreePreviewProductsContainer}`}>
                {previewProducts?.map((product: any) => (
                  <PreviewProduct
                    key={product.id}
                    product={product}
                    displayPrice={getProductPrice(product, allOptions)}
                    removeSelectedProduct={removeSelectedProduct}
                    defaultCurrency={defaultCurrency}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="border-default rounded-md mt-4">
            <SearchFTUX
              text={
                <span>
                  Selected products will
                  <br />
                  appear here
                </span>
              }
            />
          </div>
        )}
      </div>
    ) : (
      <ProductsPreviewSection
        products={orderDetails}
        selectedProductsForOrder={selectedProductsForOrder}
        allOptions={allOptions}
        defaultCurrency={defaultCurrency}
        maxQuantities={maxQuantities}
        quantities={quantities}
        setQuantities={setQuantities}
        setIsScrolling={setIsScrolling}
        isLoading={isFetchingVariants}
      />
    );
  };

  const renderFooter = () => {
    if (
      (userSelectedProducts.length > 0 || isStoreWiseSelection) &&
      activeTab === REQUEST_PRODUCT_MODAL_TABS_VALUES.AVAILABLE_PRODUCTS
    ) {
      return (
        <ModalCommonButtonsContainer
          submitButtonText={
            <span className={`${isMobile ? 'text-sm' : 'text-base'} flex items-center gap-2`}>
              <CartIcon /> Select Variants ({totalSelectedProducts})
            </span>
          }
          cancelButtonText={<span className={isMobile ? 'text-sm' : 'text-base'}>Cancel</span>}
          onClose={onClose}
          onSave={() => {
            handleSelectVariant();
          }}
          className={isMobile ? 'p-4 flex flex-col-reverse' : 'p-6'}
          disabled={isDisabled}
          loading={isFetchingVariants}
        />
      );
    }

    if (activeTab === REQUEST_PRODUCT_MODAL_TABS_VALUES.AVAILABLE_PRODUCTS || isMobile) {
      return (
        <div className={isMobile ? 'p-4' : 'p-6'}>
          <ButtonComponent variant="secondary" onClick={onClose} fullWidth>
            <span className={isMobile ? 'text-sm' : 'text-base'}>Close</span>
          </ButtonComponent>
        </div>
      );
    }

    return null;
  };

  return (
    <EnhancedModal
      open={open}
      onClose={onClose}
      tabs={finalTabs}
      activeTab={activeTab}
      onTabChange={(tabId) => {
        setActiveTab(tabId);
      }}
      title={title}
      subtitle={description}
      width={isMobile ? '100%' : 656}
      tabContainerClassName={`${styles.modalTabsContainer} ${isScrolling && 'shadow-down'}`}
      tabFontSize="16px"
      bodyClassName="p-0"
      subTitleClassName="text-xs"
      footer={renderFooter()}
      closeOnClickOutside={false}
      footerClassName={`${(selectedProductsForOrder?.length > 3 || isScrolling) && 'upper-shadow'} p-0`}
    >
      {activeTab === REQUEST_PRODUCT_MODAL_TABS_VALUES.AVAILABLE_PRODUCTS
        ? renderSelectionTab()
        : renderAlreadyRequestedTab()}
    </EnhancedModal>
  );
}
